/**
 * What an assessment can cite, per source: the case's saved investigations (already loaded by
 * the case layout), its most recent documents, the Monitor's alerts and the research runs started
 * from the hypothesis. A source still being read lists nothing yet (`null`); one that failed lists
 * nothing to cite.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  EvidenceOption,
  EvidenceSource,
} from '@/components/analytic-hypotheses/EvidencePicker'
import { monitorKeys } from '@/features/monitor/useMonitor'
import { api } from '@/services/api'
import { type AlertDto, caseMonitorApi } from '@/services/api/case-monitor'
import type { Document } from '@/services/api/cases'
import { useInvestigationsStore } from '@/stores/investigations-store-provider'
import type { HypothesisResearchRun } from '@/types/analytic-hypothesis'
import type { Investigation } from '@/types/investigation'
import { formatDate } from '@/utils/date'

/** The newest documents the picker lists (the server's page limit). */
const DOCUMENTS = 200

export type Read<T> = { data?: readonly T[]; isError: boolean }

function itemsOf<T>(read: Read<T>, toOption: (item: T) => EvidenceOption) {
  if (read.data) return read.data.map(toOption)
  return read.isError ? [] : null
}

/** No research runs to cite (a stable default, so the memo below holds). */
const NO_RUNS: Read<HypothesisResearchRun> = { data: [], isError: false }

export function useEvidenceSources(
  caseId: string,
  researchRuns: Read<HypothesisResearchRun> = NO_RUNS
): EvidenceSource[] {
  const { i18n } = useTranslation()
  const saved = useInvestigationsStore(state => state.saved)
  const investigationsLoading = useInvestigationsStore(state => state.isLoading)
  const { data: documentRows, isError: documentsFailed } = useQuery({
    queryKey: ['case', caseId, 'documents', 'evidence-picker'] as const,
    queryFn: () => api.cases.listDocuments(caseId, { limit: DOCUMENTS }),
    enabled: Boolean(caseId),
    staleTime: 60_000,
  })
  // The Monitor inbox's own cache entry, read here without its polling.
  const { data: alertRows, isError: alertsFailed } = useQuery({
    queryKey: monitorKeys.alerts(caseId),
    queryFn: async ({ signal }) => (await caseMonitorApi.listAlerts(caseId, signal)).alerts,
    enabled: Boolean(caseId),
    staleTime: 30_000,
  })

  return useMemo(() => {
    const date = (iso: string) => formatDate(iso, i18n.language)
    const investigations: Read<Investigation> = {
      data: investigationsLoading ? undefined : saved,
      isError: false,
    }
    return [
      {
        kind: 'investigation',
        items: itemsOf(investigations, inv => ({
          id: inv.id,
          label: inv.title,
          hint: date(inv.updatedAt),
        })),
      },
      {
        kind: 'document',
        items: itemsOf<Document>({ data: documentRows, isError: documentsFailed }, doc => ({
          id: doc.id,
          label: doc.filename,
          hint: date(doc.created_at),
        })),
      },
      {
        kind: 'alert',
        items: itemsOf<AlertDto>({ data: alertRows, isError: alertsFailed }, alert => ({
          id: alert.id,
          label: [alert.subject_label ?? alert.subject_uid, alert.document_name]
            .filter(Boolean)
            .join(' · '),
          hint: date(alert.recorded_at),
        })),
      },
      {
        kind: 'research_run',
        items: itemsOf(researchRuns, run => ({
          id: run.id,
          label: run.title,
          hint: date(run.started_at),
        })),
      },
    ]
  }, [
    saved,
    investigationsLoading,
    documentRows,
    documentsFailed,
    alertRows,
    alertsFailed,
    researchRuns,
    i18n.language,
  ])
}
